import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import './TournamentBracket.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TournamentBracket = ({ 
  tournamentId, 
  token, 
  user,
  onClose, 
  onJoinMatch,
  addNotification 
}) => {
  const { t } = useTranslation();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoCloseCountdown, setAutoCloseCountdown] = useState(null);

  const fetchTournament = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tournaments/${tournamentId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await response.json();
      if (data.success) {
        setTournament(data.tournament);
      } else {
        setError(data.detail || t('tournament.failedToLoadTournament'));
      }
    } catch (err) {
      setError(t('tournament.failedToLoadTournament'));
    } finally {
      setLoading(false);
    }
  }, [tournamentId, token]);

  useEffect(() => {
    fetchTournament();
    // Poll for updates every 10 seconds. Stop polling once the tournament is
    // completed or cancelled — no more state changes to watch for, and we'll
    // auto-close the bracket shortly anyway.
    const interval = setInterval(() => {
      if (tournament && ['completed', 'cancelled'].includes(tournament.status)) return;
      fetchTournament();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchTournament, tournament]);

  // When the tournament transitions to completed/cancelled, start a 15-second
  // auto-close countdown so the winner screen briefly celebrates then releases
  // all players back to the lobby without them having to hunt for a close
  // button.
  useEffect(() => {
    if (!tournament) return;
    if (!['completed', 'cancelled'].includes(tournament.status)) {
      setAutoCloseCountdown(null);
      return;
    }
    let seconds = 15;
    setAutoCloseCountdown(seconds);
    const tick = setInterval(() => {
      seconds -= 1;
      setAutoCloseCountdown(seconds);
      if (seconds <= 0) {
        clearInterval(tick);
        if (onClose) onClose();
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [tournament?.status, onClose]);

  const handleReadyForMatch = async (matchId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/tournaments/${tournamentId}/matches/${matchId}/ready`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      if (data.success) {
        addNotification?.('success', data.message);
        if (data.both_ready && data.game_id) {
          // Both players ready - join the game
          onJoinMatch?.(data.game_id);
        }
        fetchTournament();
      } else {
        setError(data.detail || t('tournament.failedToMarkReady'));
      }
    } catch (err) {
      setError(t('tournament.failedToMarkReady'));
    }
  };

  const getPlayerName = (playerId) => {
    if (!playerId || !tournament) return 'TBD';
    const participant = tournament.participants?.find(p => p.user_id === playerId);
    return participant?.user_name || 'Unknown';
  };

  const isUserInMatch = (match) => {
    return match.player1_id === user?.user_id || match.player2_id === user?.user_id;
  };

  const isUserReady = (match) => {
    if (match.player1_id === user?.user_id) return match.player1_ready;
    if (match.player2_id === user?.user_id) return match.player2_ready;
    return false;
  };

  const renderMatch = (match, roundIndex) => {
    const player1Name = getPlayerName(match.player1_id);
    const player2Name = getPlayerName(match.player2_id);
    const isMyMatch = isUserInMatch(match);
    const imReady = isUserReady(match);
    const canReady = isMyMatch && match.status === 'pending' && match.player1_id && match.player2_id;

    return (
      <div 
        key={match.match_id} 
        className={`bracket-match ${isMyMatch ? 'my-match' : ''} status-${match.status}`}
        data-testid={`match-${match.match_id}`}
      >
        <div className={`match-player ${match.winner_id === match.player1_id ? 'winner' : ''} ${match.player1_ready ? 'ready' : ''}`}>
          <span className="player-name">{player1Name}</span>
          {match.player1_ready && <span className="ready-badge">✓</span>}
          {match.winner_id === match.player1_id && <span className="winner-badge">🏆</span>}
        </div>
        <div className="match-vs">VS</div>
        <div className={`match-player ${match.winner_id === match.player2_id ? 'winner' : ''} ${match.player2_ready ? 'ready' : ''}`}>
          <span className="player-name">{player2Name}</span>
          {match.player2_ready && <span className="ready-badge">✓</span>}
          {match.winner_id === match.player2_id && <span className="winner-badge">🏆</span>}
        </div>
        
        {canReady && !imReady && (
          <button 
            className="ready-btn"
            onClick={() => handleReadyForMatch(match.match_id)}
            data-testid={`ready-btn-${match.match_id}`}
          >
            Ready Up!
          </button>
        )}
        {canReady && imReady && (
          <div className="waiting-opponent">Waiting for opponent...</div>
        )}
        {match.status === 'in_progress' && isMyMatch && (
          <button 
            className="join-match-btn"
            onClick={() => onJoinMatch?.(match.game_id)}
            data-testid={`join-match-${match.match_id}`}
          >
            Join Match
          </button>
        )}
        {match.status === 'in_progress' && !isMyMatch && (
          <button 
            className="spectate-btn"
            onClick={() => onJoinMatch?.(match.game_id, true)}
          >
            Spectate
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bracket-overlay">
        <div className="bracket-modal">
          <div className="loading">Loading tournament...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bracket-overlay">
        <div className="bracket-modal">
          <div className="error">{error}</div>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return null;
  }

  const bracket = tournament.bracket || {};
  const rounds = bracket.rounds || [];

  return (
    <div className="bracket-overlay" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="bracket-modal" data-testid="tournament-bracket">
        <button className="close-btn" onClick={onClose}>×</button>
        
        <div className="bracket-header">
          <h2>{tournament.name}</h2>
          <div className="bracket-status">
            {tournament.status === 'in_progress' && (
              <span className="status-badge in-progress">🎮 In Progress</span>
            )}
            {tournament.status === 'completed' && (
              <span className="status-badge completed">🏆 {tournament.winner_name} Wins!</span>
            )}
            {tournament.status === 'cancelled' && (
              <span className="status-badge cancelled">Tournament cancelled</span>
            )}
            {autoCloseCountdown !== null && autoCloseCountdown > 0 && (
              <span className="status-badge auto-close" data-testid="tournament-auto-close-countdown">
                Returning to lobby in {autoCloseCountdown}s…
              </span>
            )}
          </div>
        </div>

        <div className="bracket-container">
          {rounds.map((round) => (
            <div key={round.round_name} className="bracket-round">
              <h3 className="round-name">{round.round_name}</h3>
              <div className="round-matches">
                {round.matches.map((match) => renderMatch(match, roundIndex))}
              </div>
            </div>
          ))}
        </div>

        <div className="bracket-participants">
          <h4>Participants</h4>
          <div className="participants-list">
            {tournament.participants?.map((p) => (
              <div key={p.user_id} className={`participant ${p.status}`}>
                <span className="participant-name">{p.user_name}</span>
                {p.status === 'eliminated' && <span className="eliminated-badge">❌</span>}
                {p.status === 'winner' && <span className="winner-badge">🏆</span>}
                {p.user_id === tournament.host_id && <span className="host-badge">👑</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentBracket;
